/**
 * PoolFeedPanel — a pool's query feed across its lifecycle
 * (LARGE-SETS-QUERY-AND-SMART-SETS-PRD.md §4.3; migration 723).
 *
 *   unfed   → bind a rule (same vocabulary as Smart Collections).
 *   open    → the rule, its plain English, and what a refresh WOULD do:
 *             "Refresh from feed" opens a diff the person accepts or trims;
 *             only the ticked rows are written. A coverage note says what
 *             locking now would freeze out.
 *   severed → the feed was cut at lock; the membership is frozen. Items that
 *             match since wait in the Arrival Bay: hold (default), dismiss,
 *             carry into an open successor pool, or — only when the workspace
 *             has opted in — amend into the locked pool with a reason, through
 *             amend_locked_pool.
 *
 * Nothing on this panel refreshes on its own, and nothing here writes a
 * locked pool except the governed amendment.
 */

import { useMemo, useState } from 'react';
import { AlertCircle, Clock, Lock, RefreshCw, Rss, Sparkles, X } from 'lucide-react';
import { Button } from '../../ui/Button';
import { Callout, Checkbox, Field, Modal, TextArea } from '../../ds';
import { ItemPoolPickerModal } from '../item-list/components/ItemPoolPickerModal';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useItemPoolsStore } from '../../../stores/prioritization/itemPoolsStore';
import { useSavedReportsStore } from '../../../stores/prioritization/savedReportsStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { buildCatalog, printQuery, safeParse, toEnglish } from '../../../lib/pql';
import { SMART_COLLECTION_FIELDS } from '../../../lib/collections/smartCollectionFields';
import { usePoolFeed } from '../../../features/pools/usePoolFeed';
import { usePoolAmendmentEnabled } from '../../../features/pools/hooks/usePoolAmendmentEnabled';
import {
  amendLockedPool,
  applyFeedRefresh,
  bindPoolFeed,
  carryArrival,
  disposeArrival,
  unbindPoolFeed,
} from '../../../lib/poolFeedService';
import { getReportOrigin } from '../../../lib/reportDependents';
import type { ArrivalRecord } from '../../../lib/poolFeedCore';
import type { Item, ItemPool } from '../../../types/prioritization/lifecycle';

const FEED_CATALOG = buildCatalog(SMART_COLLECTION_FIELDS);

export interface PoolFeedPanelProps {
  pool: ItemPool;
  /** Items the feed is evaluated over (the workspace's items). */
  candidateItems: Item[];
  actorId?: string;
  canEdit: boolean;
}

function fmtDate(d: Date | string | null | undefined): string {
  if (!d) return '';
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function PoolFeedPanel({ pool, candidateItems, actorId, canEdit }: PoolFeedPanelProps) {
  const feed = usePoolFeed(pool, candidateItems);
  const amendmentEnabled = usePoolAmendmentEnabled();
  const addNotification = useUIStore((s) => s.addNotification);
  const updateReport = useSavedReportsStore((s) => s.updateReport);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [diffOpen, setDiffOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const parsed = useMemo(() => safeParse(draft, FEED_CATALOG), [draft]);
  const draftEnglish = parsed.ok ? toEnglish(parsed.query, FEED_CATALOG) : null;

  const ruleQuery = useMemo(() => {
    const text = feed.report?.queryText;
    if (!text) return null;
    const r = safeParse(text, FEED_CATALOG);
    return r.ok ? r.query : null;
  }, [feed.report?.queryText]);
  const ruleEnglish = ruleQuery ? toEnglish(ruleQuery, FEED_CATALOG) : null;
  // The feed's rule is a copy of whatever query it was attached from; editing
  // that query does not reach this one, so the panel says where it came from.
  const originName = pool.feedQueryId ? getReportOrigin(pool.feedQueryId)?.name ?? null : null;

  const startEditing = () => {
    setDraft(feed.report?.queryText ?? '');
    setEditing(true);
  };

  const saveRule = async () => {
    if (!parsed.ok || !draft.trim()) return;
    const canonical = printQuery(parsed.query);
    if (feed.report) {
      updateReport(feed.report.id, { queryText: canonical, queryAst: parsed.query.where });
      setEditing(false);
      addNotification({ type: 'success', title: 'Feed rule updated', message: 'Refresh to see what the new rule would change.', duration: 3000 });
      return;
    }
    const res = await bindPoolFeed(pool, canonical, parsed.query.where);
    if (res.error) {
      addNotification({ type: 'error', title: 'Could not attach feed', message: res.error, duration: 4000 });
      return;
    }
    setEditing(false);
    addNotification({ type: 'success', title: 'Feed attached', message: 'Nothing was added yet — use Refresh from feed to review the matches.', duration: 4000 });
  };

  const unbind = () => {
    const res = unbindPoolFeed(pool.id);
    if (res.error) addNotification({ type: 'error', title: 'Could not detach feed', message: res.error, duration: 4000 });
    else addNotification({ type: 'info', title: 'Feed detached', message: 'The pool keeps its members; the saved query stays in the library.', duration: 3000 });
  };

  // ── unfed ────────────────────────────────────────────────────────────────
  if (feed.phase === 'unfed' && !editing) {
    if (!canEdit) return null;
    return (
      <Section title="Query feed" phase="Not fed">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[11px] text-[var(--text-ter)]">
            Feed this pool from a query. While the pool is open you review each refresh as a diff; at lock the feed is severed and later matches wait in an Arrival Bay.
          </p>
          <Button variant="secondary" size="sm" onClick={startEditing} disabled={Boolean(pool.poolLockedAt)} title={pool.poolLockedAt ? 'Unlock the pool to attach a feed' : undefined}>
            <Rss className="h-3.5 w-3.5" /> Feed from a query
          </Button>
        </div>
      </Section>
    );
  }

  // ── rule editor (bind or edit) ────────────────────────────────────────────
  if (editing) {
    return (
      <Section title="Query feed" phase={feed.report ? 'Editing rule' : 'New feed'}>
        <div className="flex items-center gap-2">
          <Rss className="h-4 w-4 shrink-0 text-[var(--text-ter)]" />
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void saveRule();
              if (e.key === 'Escape') setEditing(false);
            }}
            autoFocus
            spellCheck={false}
            autoComplete="off"
            placeholder='e.g. itemCategory = risk AND exit_status = active AND tags ~ "q4"'
            className={`flex-1 font-mono text-[13px] border px-3 py-1.5 bg-[var(--bg-raised)] focus:outline-none focus:ring-2 ${
              !parsed.ok && draft ? 'border-[var(--coral)] focus:ring-red-100' : 'border-[var(--rule)] focus:border-[var(--slate)] focus:ring-indigo-100'
            }`}
          />
          <Button variant="cta" size="sm" onClick={() => void saveRule()} disabled={!parsed.ok || !draft.trim()}>
            {feed.report ? 'Save rule' : 'Attach feed'}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setEditing(false)} className="!px-1.5" aria-label="Cancel">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="mt-1.5 pl-6 text-[11px] leading-4">
          {!parsed.ok && draft ? (
            <span className="flex items-start gap-1 text-[var(--coral)]"><AlertCircle className="mt-px h-3 w-3 shrink-0" />{parsed.error.message}</span>
          ) : draftEnglish ? (
            <span className="italic text-[var(--text-ter)]">{draftEnglish}</span>
          ) : (
            <span className="text-[var(--text-ter)]">Fields: category, status, altitude, tags, source, title, description, created/updated, custom fields (meta.key)</span>
          )}
        </div>
      </Section>
    );
  }

  // ── open: diffed refresh ─────────────────────────────────────────────────
  if (feed.phase === 'open') {
    const { adds, removals } = feed.diff;
    const pending = adds.length + removals.length;
    return (
      <Section title="Query feed" phase="Open · refresh is explicit">
        <RuleLine text={feed.report?.queryText} english={ruleEnglish} loading={feed.loadingRule} originName={originName} />
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[var(--text-ter)]">
          <span className="tabular-nums">{feed.matchedIds.length} match{feed.matchedIds.length === 1 ? '' : 'es'} now</span>
          <span className="tabular-nums">{adds.length} to add</span>
          <span className="tabular-nums">{removals.length} no longer match</span>
          {pool.feedLastRefreshedAt && <span>Last refreshed {fmtDate(pool.feedLastRefreshedAt)}</span>}
          <span className="ml-auto flex items-center gap-1">
            {canEdit && (
              <>
                <Button variant="cta" size="sm" onClick={() => setDiffOpen(true)} disabled={pending === 0 || !feed.report} className="!py-1 !text-[11px]">
                  <RefreshCw className="h-3 w-3" /> Refresh from feed{pending > 0 ? ` (${pending})` : ''}
                </Button>
                <Button variant="ghost" size="sm" onClick={startEditing} className="!py-1 !text-[11px]">Edit rule</Button>
                <Button variant="ghost" size="sm" onClick={unbind} className="!py-1 !text-[11px]">Detach</Button>
              </>
            )}
          </span>
        </div>
        {feed.coverage.warning && (
          <Callout tone="mustard" className="mt-2" title="Before you lock">
            {feed.coverage.warning}
          </Callout>
        )}
        {diffOpen && feed.report && (
          <RefreshDiffModal
            pool={pool}
            adds={adds}
            removals={removals}
            busy={busy}
            onClose={() => setDiffOpen(false)}
            onApply={(acceptedAdds, acceptedRemovals) => {
              setBusy(true);
              try {
                const res = applyFeedRefresh({ pool, adds: acceptedAdds, removals: acceptedRemovals, actorId });
                if (res.error) {
                  addNotification({ type: 'error', title: 'Refresh not applied', message: res.error, duration: 4000 });
                } else {
                  addNotification({
                    type: 'success',
                    title: 'Pool refreshed from feed',
                    message: `${res.added.length} added, ${res.removed.length} removed. Each addition carries the query and the moment it was evaluated.`,
                    duration: 4000,
                  });
                  setDiffOpen(false);
                }
              } finally {
                setBusy(false);
              }
            }}
          />
        )}
      </Section>
    );
  }

  // ── severed: the Arrival Bay ─────────────────────────────────────────────
  return (
    <SeveredFeed
      pool={pool}
      feed={feed}
      ruleEnglish={ruleEnglish}
      actorId={actorId}
      canEdit={canEdit}
      amendmentEnabled={amendmentEnabled}
    />
  );
}

// ── pieces ──────────────────────────────────────────────────────────────────

function Section({ title, phase, children }: { title: string; phase: string; children: React.ReactNode }) {
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-4 py-3">
      <div className="flex items-center gap-2 mb-2">
        <Rss className="h-4 w-4 text-[var(--text-ter)]" />
        <span className="text-sm font-medium text-[var(--text-sec)]">{title}</span>
        <span className="text-[10px] uppercase tracking-wide text-[var(--text-ter)]">· {phase}</span>
      </div>
      {children}
    </div>
  );
}

function RuleLine({ text, english, loading, originName }: { text: string | undefined; english: string | null; loading: boolean; originName?: string | null }) {
  if (loading) return <p className="text-[11px] text-[var(--text-ter)]">Loading the feed rule…</p>;
  if (!text) {
    return (
      <p className="flex items-center gap-1 text-[11px] text-[var(--coral)]">
        <AlertCircle className="h-3 w-3" /> The saved query behind this feed is missing. Detach and attach a new rule.
      </p>
    );
  }
  return (
    <div>
      <code className="block font-mono text-[12px] text-[var(--text-pri)] break-words">{text}</code>
      {english && <p className="mt-0.5 text-[11px] italic text-[var(--text-ter)]">{english}</p>}
      {originName && (
        <p className="mt-0.5 text-[10px] text-[var(--text-ter)]">
          Copied from the saved query “{originName}”. This feed is its own — editing that query does not change it.
        </p>
      )}
    </div>
  );
}

function ItemTitle({ id }: { id: string }) {
  const item = useItemsStore((s) => s.getItemById(id));
  return <span className="truncate">{item?.title ?? id}</span>;
}

function RefreshDiffModal({
  pool,
  adds,
  removals,
  busy,
  onClose,
  onApply,
}: {
  pool: ItemPool;
  adds: string[];
  removals: string[];
  busy: boolean;
  onClose: () => void;
  onApply: (acceptedAdds: string[], acceptedRemovals: string[]) => void;
}) {
  // Adds default on (that is what a feed is for); removals default OFF — a
  // person removes a member on purpose, never because a rule drifted.
  const [addSet, setAddSet] = useState(() => new Set(adds));
  const [removeSet, setRemoveSet] = useState(() => new Set<string>());
  const toggle = (set: Set<string>, id: string, setter: (s: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  };
  const total = addSet.size + removeSet.size;
  return (
    <Modal
      open
      onClose={onClose}
      title="Refresh from feed"
      description={`What the query would change in "${pool.name}". Only what you tick is written.`}
      size="md"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="cta" size="sm" onClick={() => onApply([...addSet], [...removeSet])} disabled={busy || total === 0}>
            {busy ? 'Applying…' : `Apply ${total} change${total === 1 ? '' : 's'}`}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <DiffGroup
          heading={`Add — ${adds.length} now match${adds.length === 1 ? 'es' : ''} and ${adds.length === 1 ? 'is' : 'are'} not in the pool`}
          ids={adds}
          selected={addSet}
          onToggle={(id) => toggle(addSet, id, setAddSet)}
          onAll={(on) => setAddSet(on ? new Set(adds) : new Set())}
          empty="Nothing new matches."
        />
        <DiffGroup
          heading={`Remove — ${removals.length} member${removals.length === 1 ? '' : 's'} the feed no longer match${removals.length === 1 ? 'es' : ''}`}
          ids={removals}
          selected={removeSet}
          onToggle={(id) => toggle(removeSet, id, setRemoveSet)}
          onAll={(on) => setRemoveSet(on ? new Set(removals) : new Set())}
          empty="Every member still matches."
          hint="Unticked by default. Removing a member is a decision, not a side effect of the rule."
        />
      </div>
    </Modal>
  );
}

function DiffGroup({
  heading,
  ids,
  selected,
  onToggle,
  onAll,
  empty,
  hint,
}: {
  heading: string;
  ids: string[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  onAll: (on: boolean) => void;
  empty: string;
  hint?: string;
}) {
  const all = ids.length > 0 && ids.every((id) => selected.has(id));
  const some = ids.some((id) => selected.has(id));
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-[var(--text-sec)]">{heading}</span>
        {ids.length > 1 && (
          <Checkbox label={all ? 'None' : 'All'} checked={all} indeterminate={some && !all} onChange={() => onAll(!all)} />
        )}
      </div>
      {hint && <p className="text-[11px] text-[var(--text-ter)] mt-0.5">{hint}</p>}
      {ids.length === 0 ? (
        <p className="text-[11px] text-[var(--text-ter)] mt-1">{empty}</p>
      ) : (
        <ul className="mt-1 max-h-48 overflow-y-auto divide-y divide-[var(--rule)] border border-[var(--rule)]">
          {ids.map((id) => (
            <li key={id} className="px-2 py-1 text-[12px]">
              <Checkbox checked={selected.has(id)} onChange={() => onToggle(id)} label={<ItemTitle id={id} />} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SeveredFeed({
  pool,
  feed,
  ruleEnglish,
  actorId,
  canEdit,
  amendmentEnabled,
}: {
  pool: ItemPool;
  feed: ReturnType<typeof usePoolFeed>;
  ruleEnglish: string | null;
  actorId?: string;
  canEdit: boolean;
  amendmentEnabled: boolean;
}) {
  const addNotification = useUIStore((s) => s.addNotification);
  const getItemPoolById = useItemPoolsStore((s) => s.getItemPoolById);
  const [carrying, setCarrying] = useState<string[] | null>(null);
  const [amending, setAmending] = useState<string[] | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const held = feed.bay.held;
  const stale = new Set(feed.bay.staleHeld);

  const dismiss = async (itemId: string) => {
    const res = await disposeArrival({ poolId: pool.id, itemId, disposition: 'dismissed', actorId });
    if (res.error) addNotification({ type: 'error', title: 'Could not dismiss', message: res.error, duration: 4000 });
    await feed.reloadArrivals();
  };

  const carryInto = async (targetPoolId: string) => {
    const ids = carrying ?? [];
    setCarrying(null);
    const toPool = getItemPoolById(targetPoolId);
    if (!toPool) return;
    setBusy(true);
    let ok = 0;
    let firstError: string | null = null;
    try {
      for (const itemId of ids) {
        const res = await carryArrival({ fromPool: pool, toPool, itemId, actorId });
        if (res.error) firstError ??= res.error; else ok++;
      }
    } finally {
      setBusy(false);
    }
    await feed.reloadArrivals();
    addNotification(
      firstError
        ? { type: 'warning', title: `Carried ${ok} of ${ids.length}`, message: firstError, duration: 5000 }
        : { type: 'success', title: `Carried into ${toPool.name}`, message: `${ok} item${ok === 1 ? '' : 's'} moved to the next assembly.`, duration: 4000 },
    );
  };

  const amend = async () => {
    const ids = amending ?? [];
    if (ids.length === 0 || !reason.trim()) return;
    setBusy(true);
    try {
      const res = await amendLockedPool(pool.id, ids, reason.trim());
      if (res.error) {
        addNotification({ type: 'error', title: 'Amendment refused', message: res.error, duration: 6000 });
        return;
      }
      setAmending(null);
      setReason('');
      await feed.reloadArrivals();
      addNotification({
        type: 'success',
        title: 'Locked pool amended',
        message: `${res.added.length} item${res.added.length === 1 ? '' : 's'} appended to the single list. ${res.sessionsFlagged} in-progress session${res.sessionsFlagged === 1 ? '' : 's'} flagged for re-baseline.`,
        duration: 6000,
      });
    } finally {
      setBusy(false);
    }
  };

  const severedAt = pool.feedSeveredAt ?? pool.poolLockedAt;
  const past = feed.bay.dismissed.length + feed.bay.carried.length + feed.bay.amended.length;
  const originName = pool.feedQueryId ? getReportOrigin(pool.feedQueryId)?.name ?? null : null;

  return (
    <Section title="Query feed" phase="Severed at lock">
      <RuleLine text={feed.report?.queryText} english={ruleEnglish} loading={feed.loadingRule} originName={originName} />
      <Callout tone="slate" className="mt-2" title={<span className="inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Membership frozen</span>}>
        The feed was severed {severedAt ? `on ${fmtDate(severedAt)}` : 'at lock'}. {pool.priorityIds.length} item{pool.priorityIds.length === 1 ? '' : 's'} are the one list every group ranks. New matches wait below; nothing joins the ranked list without a governed amendment.
      </Callout>

      <div className="mt-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold text-[var(--text-sec)] inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5 text-[var(--text-ter)]" /> Arrival Bay · {held.length} held
          </span>
          {canEdit && held.length > 1 && (
            <span className="flex items-center gap-1">
              <Button variant="secondary" size="sm" className="!py-1 !text-[11px]" onClick={() => setCarrying(held.map((a) => a.itemId))} disabled={busy}>
                Carry all to next pool…
              </Button>
              {amendmentEnabled && (
                <Button variant="secondary" size="sm" className="!py-1 !text-[11px]" onClick={() => setAmending(held.map((a) => a.itemId))} disabled={busy}>
                  Amend all…
                </Button>
              )}
            </span>
          )}
        </div>
        {held.length === 0 ? (
          <p className="mt-1 text-[11px] text-[var(--text-ter)]">Nothing has arrived since the lock.</p>
        ) : (
          <ul className="mt-1 divide-y divide-[var(--rule)] border border-[var(--rule)]">
            {held.map((a: ArrivalRecord) => (
              <li key={a.itemId} className="flex items-center gap-2 px-3 py-1.5 text-[12px]">
                <ItemTitle id={a.itemId} />
                {stale.has(a.itemId) && (
                  <span className="text-[10px] uppercase tracking-wide text-[var(--mustard)]" title="The feed no longer matches this item; it stays held until you decide">no longer matches</span>
                )}
                <span className="ml-auto text-[10px] text-[var(--text-ter)] whitespace-nowrap">arrived {fmtDate(a.matchedAt)}</span>
                {canEdit && (
                  <span className="flex items-center gap-0.5">
                    <Button variant="ghost" size="sm" className="!py-0.5 !px-1.5 !text-[11px]" onClick={() => setCarrying([a.itemId])} disabled={busy}>Carry…</Button>
                    {amendmentEnabled && (
                      <Button variant="ghost" size="sm" className="!py-0.5 !px-1.5 !text-[11px]" onClick={() => setAmending([a.itemId])} disabled={busy}>Amend…</Button>
                    )}
                    <Button variant="ghost" size="sm" className="!py-0.5 !px-1.5 !text-[11px]" onClick={() => void dismiss(a.itemId)} disabled={busy}>Dismiss</Button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {past > 0 && (
          <p className="mt-1 text-[10px] text-[var(--text-ter)]">
            Earlier: {feed.bay.carried.length} carried · {feed.bay.amended.length} amended · {feed.bay.dismissed.length} dismissed
          </p>
        )}
        {!amendmentEnabled && held.length > 0 && (
          <p className="mt-1 text-[10px] text-[var(--text-ter)]">
            Held items wait for the next cycle. Mid-cycle amendment is switched off for this workspace.
          </p>
        )}
      </div>

      <ItemPoolPickerModal open={carrying !== null} onClose={() => setCarrying(null)} onSelect={(id) => void carryInto(id)} />

      {amending && (
        <Modal
          open
          onClose={() => { setAmending(null); setReason(''); }}
          title="Amend the locked pool"
          description={`Append ${amending.length} item${amending.length === 1 ? '' : 's'} to "${pool.name}" after lock. This is a governance act: it is recorded with your reason, and in-progress sessions on this track are flagged to re-baseline.`}
          size="sm"
          footer={
            <>
              <Button variant="ghost" size="sm" onClick={() => { setAmending(null); setReason(''); }} disabled={busy}>Cancel</Button>
              <Button variant="danger" size="sm" onClick={() => void amend()} disabled={busy || !reason.trim()}>
                {busy ? 'Amending…' : 'Amend pool'}
              </Button>
            </>
          }
        >
          <Field label="Reason" required htmlFor="amend-reason" hint="Written into the governance record and each membership row.">
            <TextArea id="amend-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Why this must join the ranked list now rather than next cycle" />
          </Field>
          <p className="mt-2 inline-flex items-center gap-1 text-[11px] text-[var(--text-ter)]"><Sparkles className="h-3 w-3" /> Every ranking group sees the amendment at the same instant — there is one list.</p>
        </Modal>
      )}
    </Section>
  );
}
